import { useCallback, type ReactNode } from 'react'
import { useConfirm } from '@/components/patterns/useConfirm'
import { runWithFeedback } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { listMerges, undoMerge } from './api'
import type { MergeKind, UndoResult } from './types'

const MAX_KEPT_LINES = 5

/** The toast description: what stayed with the survivor, at most five lines and "+ n meer". */
export function keptLines(result: UndoResult): string {
  const lines = result.kept
    .slice(0, MAX_KEPT_LINES)
    .map((item) => `${item.naam}: ${item.reden.charAt(0).toLowerCase()}${item.reden.slice(1)}`)
  if (result.kept.length > MAX_KEPT_LINES) lines.push(`+ ${result.kept.length - MAX_KEPT_LINES} meer`)
  return lines.join('\n')
}

/** Undoes one merge and says how it went; resolves to the result, or to undefined after a failure. */
export async function performUndo(mergeId: string): Promise<UndoResult | undefined> {
  const result = await runWithFeedback(() => undoMerge(mergeId), { error: 'Ongedaan maken mislukt.' })
  if (!result) return undefined
  if (result.kept.length === 0) {
    toast.success('Samenvoeging ongedaan gemaakt')
  } else {
    const count = result.kept.length
    toast.success(`Ongedaan gemaakt · ${count === 1 ? '1 ding bleef' : `${count} dingen bleven`} staan`, {
      description: keptLines(result),
    })
  }
  return result
}

/** Undoes the newest merge of the survivor that can still be undone (the toast after a merge). */
export async function undoLatestMerge(kind: MergeKind, survivorId: string): Promise<UndoResult | undefined> {
  const items = await listMerges(kind, survivorId)
  const latest = items.find((item) => item.can_undo)
  if (!latest) throw new Error('Deze samenvoeging kan niet meer ongedaan worden gemaakt.')
  return performUndo(latest.id)
}

/** The toast after a merge, with "Ongedaan maken" that undoes it. */
export function toastMerged(
  kind: MergeKind,
  survivorId: string,
  message: string,
  onUndone?: (result: UndoResult) => void,
): void {
  toastUndo(message, async () => {
    const result = await undoLatestMerge(kind, survivorId)
    if (result) onUndone?.(result)
  })
}

export interface UndoRequest {
  mergeId: string
  survivorName: string
  mergedName: string
  kind: MergeKind
}

/** Asks first ("Samenvoeging ongedaan maken?"), then undoes. Render the returned dialog once. */
export function useUndoMerge(): [ReactNode, (request: UndoRequest) => Promise<UndoResult | undefined>] {
  const [dialog, confirm] = useConfirm()
  const undo = useCallback(
    async (request: UndoRequest) => {
      const ok = await confirm({
        title: 'Samenvoeging ongedaan maken?',
        description: `${request.mergedName} komt terug als ${request.kind === 'contact' ? 'los contact' : 'losse organisatie'}. Wat sindsdien bij ${request.survivorName} is toegevoegd, blijft daar.`,
        confirmLabel: 'Ongedaan maken',
        destructive: false,
      })
      if (!ok) return undefined
      return performUndo(request.mergeId)
    },
    [confirm],
  )
  return [dialog, undo]
}
